import React, { useMemo, useState } from 'react';
import { Eye, Monitor, Smartphone, Tablet, X } from 'lucide-react';
import { useProcessTeam } from '../workforce/useProcessTeam';
import { personName } from '../workforce/orgChartModel';
import { useSandboxSession } from '../sandbox/SandboxSessionContext';
import { getDraftTitle } from '../sandbox/utils/draftPreviewPresentation';
import { useViewAs } from './ViewAsContext';

const DEVICE_BUTTONS = [
  { device: 'desktop', label: 'Desktop', Icon: Monitor },
  { device: 'tablet', label: 'Tablet', Icon: Tablet },
  { device: 'phone', label: 'Phone', Icon: Smartphone },
];

const select = 'h-8 max-w-[14rem] truncate rounded-lg border border-amber-300 bg-white px-2 text-sm text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600';

/** Roles first, then the active people in them; a role's demo profile is the role itself. */
export function viewAsChoices(team = {}) {
  const roles = (team.roles || []).filter((role) => role.slug !== 'admin');
  const people = roles.flatMap((role) => (role.members || [])
    .filter((member) => member.is_active && member.membership_active !== false
      && String(member.id) !== String(role.demo_user_profile_id || ''))
    .map((member) => ({ target: `person:${member.id}`, label: personName(member), role: role.name })));
  return {
    roles: roles.map((role) => ({ target: `role:${role.id}`, label: role.name })),
    people: people.sort((a, b) => a.label.localeCompare(b.label)),
  };
}

export default function ViewAsBanner() {
  const viewAs = useViewAs();
  const team = useProcessTeam(Boolean(viewAs?.viewer));
  const { sandboxes = [], previewSandboxId, selectPreviewSandbox, clearPreview } = useSandboxSession();
  const [error, setError] = useState(null);
  const [switching, setSwitching] = useState(false);
  const choices = useMemo(() => viewAsChoices(team.data), [team.data]);
  if (!viewAs?.viewer) return null;
  const { viewer, start, exit, setDevice } = viewAs;
  const device = viewer.device || 'desktop';
  const knownTarget = [...choices.roles, ...choices.people].some((choice) => choice.target === viewer.target);

  const switchTo = async (target) => {
    if (!target || target === viewer.target) return;
    setError(null);
    setSwitching(true);
    try {
      await start(target, { path: '/app/home' });
    } catch (failure) {
      setError(failure.message);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <section aria-label="Viewing as" data-testid="view-as-banner"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-950">
      <Eye size={16} aria-hidden="true" className="shrink-0 text-amber-800" />
      <label htmlFor="view-as-who" className="font-medium">Viewing as</label>
      <select id="view-as-who" className={select} value={viewer.target} disabled={switching}
        onChange={(event) => switchTo(event.target.value)}>
        {!knownTarget && <option value={viewer.target}>{viewer.person_name || viewer.role_name}</option>}
        {choices.roles.length > 0 && <optgroup label="Roles">
          {choices.roles.map((choice) => <option key={choice.target} value={choice.target}>{choice.label}</option>)}
        </optgroup>}
        {choices.people.length > 0 && <optgroup label="People">
          {choices.people.map((choice) => <option key={choice.target} value={choice.target}>{choice.label} · {choice.role}</option>)}
        </optgroup>}
      </select>
      <select aria-label="Version" className={select} value={previewSandboxId || 'live'}
        onChange={(event) => (event.target.value === 'live' ? clearPreview() : selectPreviewSandbox(event.target.value))}>
        <option value="live">Live</option>
        {sandboxes.map((sandbox) => <option key={sandbox.sandbox_id} value={sandbox.sandbox_id}>Draft: {getDraftTitle(sandbox)}</option>)}
      </select>
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
      <span className="flex-1" />
      <div role="group" aria-label="Screen size" className="flex items-center rounded-lg bg-amber-200/60 p-0.5">
        {DEVICE_BUTTONS.map(({ device: option, label, Icon }) => (
          <button key={option} type="button" aria-label={label} title={label} aria-pressed={device === option}
            onClick={() => setDevice(option)}
            className={`rounded-md p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 ${device === option ? 'bg-white text-amber-950 shadow-sm' : 'text-amber-800 hover:bg-amber-100'}`}>
            <Icon size={15} aria-hidden="true" />
          </button>
        ))}
      </div>
      <button type="button" onClick={() => void exit()}
        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-medium hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600">
        <X size={14} aria-hidden="true" />Exit
      </button>
    </section>
  );
}
